import { ClickAwayListener } from "@mui/material";
import Box from "@mui/material/Box";
import LanguageContext from "components/LanguageContext";
import MessageContext from "components/MessageContext";
import { listCityStyled, suggestionStyled } from "components/templatesComponents/StyledComponent";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useState } from "react";
import scrollIntoView from "scroll-into-view-if-needed";
import ComedecService from "services/ComedecService";
import getComedecCities from "utils/comedecUtils";
import { debounce } from "utils/commonUtils";
import t from "utils/locales/translation.json";

const listboxId = "city-search-results";

const SearchBar = (props) => {
  const { postalCode = "", codeDepartement = "", setCity } = props;

  const { language } = useContext(LanguageContext);
  const { displayError } = useContext(MessageContext);

  const [searchValue, setSearchValue] = useState("");
  const [allCities, setAllCities] = useState([]);
  const [cursor, setCursor] = useState(0);
  const [refList, setRefList] = useState({});
  const [mouseOver, setMouseOver] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const hasSuggestions = Array.isArray(allCities) && allCities.length > 0;
  const showSuggestions = hasSuggestions && searchExpanded;
  const activeOptionId = hasSuggestions ? `city-search-option-${cursor}` : undefined;
  const suggestionsMessage = hasSuggestions
    ? `${allCities.length}${t[language].comedec.suggestions_message[allCities.length > 1 ? 1 : 0]}`
    : "";
  const getOptionLabel = (city) => {
    const cityCode = city.codesPostaux ? city.codesPostaux[0] : city.code;

    return cityCode ? `${city.nom}, ${cityCode}` : city.nom;
  };

  useEffect(() => {
    if (postalCode && postalCode !== codeDepartement) {
      setSearchValue("");
      setCity({});
    }
  }, [postalCode, codeDepartement, setCity]);

  const getAllCities = async (val) => {
    try {
      const results = await ComedecService.getCities({ city: val });
      let { data: cities } = results;
      if (postalCode) {
        cities = (Array.isArray(cities) && cities.filter((d) => d.codeDepartement === postalCode)) || [];
      }
      const comedecCities = await getComedecCities({ val, postalCode });
      const citiesExistedName = cities.map((c) => c.nom);
      const citiesExistedCode = cities.map((c) => c.code);
      if (Array.isArray(comedecCities)) {
        comedecCities.map((cl) => {
          return (citiesExistedName.includes(cl.nom) && citiesExistedCode.includes(cl.code)) ||
            (postalCode && cl.codeDepartement !== postalCode)
            ? null
            : cities.push(cl);
        });
      }
      cities.sort((a, b) => a.nom.localeCompare(b.nom));
      setAllCities(cities || []);
    } catch {
      try {
        const cities = [];
        const comedecCities = await getComedecCities({ val, postalCode });
        if (Array.isArray(comedecCities)) {
          comedecCities.map((cl) => {
            return postalCode && cl.codeDepartement !== postalCode ? null : cities.push(cl);
          });
        }
        cities.sort((a, b) => a.nom.localeCompare(b.nom));
        setAllCities(cities);
      } catch {
        displayError(t[language].common.search.error_api_message);
        setAllCities([]);
      }
    }
  };

  const handleInputChange = (e) => {
    const { value } = e.target;
    setSearchValue(value);
    setCursor(0);
    if (value && value.length > 1) {
      setSearchExpanded(true);
      debounce(getAllCities, 350)(value);
    } else {
      setAllCities([]);
    }
  };

  useEffect(() => {
    const refs = {};
    if (allCities?.length > 0) {
      allCities.forEach((item, i) => {
        refs[i] = React.createRef();
      });
      setRefList(refs);
    }
  }, [allCities]);

  const handleKey = (e) => {
    const ref = refList[cursor] && refList[cursor].current;
    setMouseOver(false);
    if (e.keyCode === 38 /* Arrow-up */ && cursor > 0) {
      setCursor((prevState) => prevState - 1);
      scrollIntoView(ref, {
        scrollMode: "if-needed",
        block: "end",
        inline: "nearest",
      });
    } else if (e.keyCode === 40 /* Arrow-down */ && cursor < allCities.length - 1) {
      setCursor((prevState) => prevState + 1);
      scrollIntoView(ref, {
        scrollMode: "if-needed",
        block: "start",
        inline: "nearest",
      });
    } else if (e.keyCode === 13 /* Enter */) {
      setCity(allCities[cursor] || {});
      setSearchValue(allCities[cursor] ? allCities[cursor].nom : "");
      setAllCities([]);
    }
  };

  const handleMouseOver = () => {
    setMouseOver(!mouseOver);
  };

  return (
    <ClickAwayListener onClickAway={() => setSearchExpanded(false)}>
      <Box sx={{ flexGrow: 1, position: "relative" }}>
        <div className="fr-input-group fr-mb-0" id="city-search-group">
          <label className="fr-label" htmlFor="city-search" id="city-search-label">
            {t[language].comedec.city}
          </label>
          <input
            className="fr-input"
            aria-autocomplete="list"
            aria-controls={showSuggestions ? listboxId : undefined}
            aria-activedescendant={activeOptionId}
            aria-expanded={showSuggestions}
            id="city-search"
            type="text"
            autoComplete="off"
            role="combobox"
            value={searchValue}
            onChange={handleInputChange}
            onFocus={() => setSearchExpanded(hasSuggestions)}
            onKeyDown={handleKey}
          />
          <p id="search-city-input" className="fr-sr-only" aria-live="polite">
            {suggestionsMessage}
          </p>
        </div>
        {showSuggestions && (
          <Box
            id={listboxId}
            component="ul"
            role="listbox"
            aria-labelledby="city-search-label"
            onMouseEnter={handleMouseOver}
            onMouseLeave={handleMouseOver}
            sx={listCityStyled({ top: 72 })}
          >
            {allCities.map((city, i) => (
              <Box
                id={`city-search-option-${i}`}
                component="li"
                role="option"
                aria-selected={cursor === i}
                aria-label={getOptionLabel(city)}
                key={`${city.code}`}
                ref={refList[i]}
                onClick={() => {
                  setCity(city);
                  setSearchValue(city.nom);
                  setAllCities([]);
                }}
                sx={suggestionStyled(cursor === i)}
              >
                {city.nom}
                <Box component="span" className="fr-text--sm fr-mb-0" sx={{ color: "var(--text-mention-grey)" }}>
                  {city.codesPostaux ? city.codesPostaux[0] : city.code}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

SearchBar.propTypes = {
  postalCode: PropTypes.string,
  codeDepartement: PropTypes.string,
  setCity: PropTypes.func.isRequired,
};

export default SearchBar;
